import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react';
import { Capacitor } from '@capacitor/core';
import { APIAuthenticatedService } from './APICallsAuthenticated';
import { ConfigInfoService, useConfigInfoService } from './ConfigInfo';
import { getAuth0LogoutReturnTo, getAuth0OpenUrlOption } from './auth0Capacitor';

export const PUSH_LAST_RECEIVED_TOKEN_KEY = 'push:lastReceivedToken';
export const PUSH_LAST_REGISTERED_TOKEN_KEY = 'push:lastRegisteredToken';

type LogoutWithPushCleanupOptions = Pick<
  Auth0ContextInterface,
  'getAccessTokenSilently' | 'getIdTokenClaims' | 'logout'
> & {
  configInfoService: ConfigInfoService;
  returnToPath?: string;
};

const deregisterPushTokenWithBackend = async (
  deviceToken: string,
  configInfoService: ConfigInfoService,
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'],
  getIdTokenClaims: Auth0ContextInterface['getIdTokenClaims']
) => {
  if (!Capacitor.isNativePlatform() || !deviceToken) {
    return;
  }

  try {
    const apiService = APIAuthenticatedService.getInstance(
      () => getAccessTokenSilently(),
      getIdTokenClaims,
      configInfoService
    );

    await apiService.init();
    await apiService.deregisterPushDevice(deviceToken);
    console.log('[push] token deregistered with backend');
  } catch (error: any) {
    const statusCode = error?.response?.status ?? 'unknown';
    // REQ-047: avoid logging Axios error objects because request config can include device tokens.
    console.log(`[push] failed to deregister token with backend (status: ${statusCode})`);
  }
};

export const logoutWithPushCleanup = async ({
  configInfoService,
  getAccessTokenSilently,
  getIdTokenClaims,
  logout,
  returnToPath = '',
}: LogoutWithPushCleanupOptions): Promise<void> => {
  const deviceToken = window.localStorage.getItem(PUSH_LAST_RECEIVED_TOKEN_KEY) || '';

  try {
    window.localStorage.removeItem(PUSH_LAST_REGISTERED_TOKEN_KEY);
    await deregisterPushTokenWithBackend(
      deviceToken,
      configInfoService,
      getAccessTokenSilently,
      getIdTokenClaims
    );
  } finally {
    logout({
      logoutParams: {
        returnTo: getAuth0LogoutReturnTo(process.env.REACT_APP_AUTH0_DOMAIN || '', returnToPath),
      },
      ...getAuth0OpenUrlOption(),
    });
  }
};

export const useLogoutWithPushCleanup = (returnToPath = '') => {
  const { getAccessTokenSilently, getIdTokenClaims, logout } = useAuth0();
  const configInfoService = useConfigInfoService();

  return async () =>
    logoutWithPushCleanup({
      configInfoService,
      getAccessTokenSilently,
      getIdTokenClaims,
      logout,
      returnToPath,
    });
};
